import { reportPush } from "../api/message";
import IMWebSocketClient from "../client/socketClient";
import { MANUAL_CLOSE_CODE, SocketErrorCode } from "../constants";
import { useMessageStore } from "../store";
import { transferSendContent } from "../store/formatMessage";
import {
  CurrentChatStatus,
  HeaderType,
  IFeMessage,
  Message,
  MessageNotRender,
  MessageSender,
  MessageStatus,
  MessageType,
  SendMessageOptions,
  SocketConnectionStatus,
} from "../store/types";
import { removeObjectKeys } from "../utils/object";
import { createClientUUid } from "../utils/string";
import Locale from "@/app/locales";

class WebSocketService {
  private static instance: WebSocketService;
  private wsClient: IMWebSocketClient | null = null;

  private constructor() {}

  private initCallback?: (status: SocketConnectionStatus) => void;

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  // 撤回消息方法
  public withdrawMessage(messageId: string, msgSender: MessageSender) {
    if (!this.wsClient || !this.isConnected()) {
      console.error("无法撤回消息: 连接未就绪");
      return false;
    }

    // 更新本地状态
    useMessageStore.getState().withdrawMessage(messageId, msgSender);
    return true;
  }

  // 初始化 WebSocket 连接
  public initConnection(
    serverUrl: string,
    initCallback?: (status: SocketConnectionStatus) => void,
  ) {
    if (this.wsClient) {
      console.warn("WebSocket 连接已存在，先关闭现有连接");
      this.closeConnection();
    }

    this.wsClient = new IMWebSocketClient({
      url: serverUrl,
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      ackTimeout: 5000,
      maxRetries: 3,

      onReadyStateChange: (status) => {
        useMessageStore
          .getState()
          .setConnectionStatus(status as SocketConnectionStatus);
        if (status === SocketConnectionStatus.CONNECTED) {
          window.cacheErrorInfo = {};
        }
        this.initCallback = initCallback;
        if (typeof this.initCallback === "function") {
          this.initCallback(status as SocketConnectionStatus);
        }
      },

      onMessage: (message) => {
        const newMessage = {
          ...message,
          id: message.id,
          content: message.content,
          senderType: message.senderType,
          clientId: message.clientId,
          timestamp: message.timestamp,
          type: message.type,
          status: MessageStatus.Success,
        };

        useMessageStore.getState().addIncomingMessage(newMessage);
      },

      onMessageAck: (clientId, ackMessage?: Message) => {
        const replaceContent = ackMessage?.content
          ? { content: ackMessage.content }
          : {};
        useMessageStore
          .getState()
          .updateMessageStatus(
            clientId,
            { status: MessageStatus.Success, ...replaceContent },
            ackMessage!.id as string,
          );
      },

      onCommonMessageHandler: (msg) => {
        useMessageStore.getState().setCommonMessage(msg);
      },

      onMessageFailure: (clientId) => {
        useMessageStore
          .getState()
          .updateMessageStatus(clientId, { status: MessageStatus.Failed });
      },

      onError: (error) => {
        console.error("WebSocket错误:", error);
        useMessageStore
          .getState()
          .setConnectionStatus(SocketConnectionStatus.ERROR);
        useMessageStore
          .getState()
          .setCurrentChatStatus(CurrentChatStatus.ChatEnd);
      },

      onClose: async (event) => {
        if (event.code === MANUAL_CLOSE_CODE) {
          // 插入一条消息
          this.sendMessage(`{"text": "${Locale.systemMsg.messageEnded}"}`, {
            msgType: MessageType.MsgEnd,
            noNeedSendToWebsocket: true,
          });
        }

        if (SocketErrorCode.ChatTimeout === event.code) {
          this.sendMessage(`{"text": "${Locale.systemMsg.messageTimeout}"}`, {
            msgType: MessageType.MsgEnd,
            noNeedSendToWebsocket: true,
          });
        }

        if (SocketErrorCode.LineUpTimeout === event.code) {
          this.sendMessage(`{"text": "${Locale.systemMsg.lineUpTimeout}"}`, {
            msgType: MessageType.MsgEnd,
            noNeedSendToWebsocket: true,
          });
        }

        if (
          [
            SocketErrorCode.CloseChatByAgent,
            SocketErrorCode.ChatTimeout,
          ].includes(event.code)
        ) {
          const { conversationId } = useMessageStore.getState();
          if (conversationId) {
            const pushSatisfy = await reportPush(conversationId);
            // 通过接口判断是否需要推送满意度
            if (pushSatisfy) {
              webSocketService.sendMessage("", {
                senderType: MessageSender.USER,
                msgType: MessageType.Satisfy,
                noNeedSendToWebsocket: true,
              });
            }
          }
        }

        window.cacheErrorInfo = {
          code: event.code,
          reason: event.reason,
        };
        // 更新isConnected为CLOSE
        useMessageStore
          .getState()
          .setConnectionStatus(SocketConnectionStatus.CLOSE);
      },
    });

    window.win_ws = this.wsClient;
  }

  // 重发消息方法
  public retrySendMessage(
    message: Message,
    callback: (isError: boolean) => void,
  ): string | undefined {
    if (!this.wsClient || !this.isConnected()) {
      console.error("无法重发消息: 连接未就绪");
      callback(true);
      return;
    }

    const clientId = message.clientId;

    // 更新消息状态为发送中
    useMessageStore.getState().retrySendMessage(message);

    // 创建重发消息对象
    const retryMessage = {
      ...message,
      timestamp: Date.now(),
      retryCount: (message.retryCount || 0) + 1,
    };

    // 发送消息
    this.wsClient.sendMessage(retryMessage);

    return clientId;
  }

  // 发送消息
  public async sendMessage(
    content: string,
    opts: SendMessageOptions & IFeMessage = {},
  ): Promise<undefined> {
    content = transferSendContent(content);
    const { quotedMsg, setQuotedMsg, conversationId } =
      useMessageStore.getState();
    let quotedMsgData = {};
    if (quotedMsg && !quotedMsg.hasRedrawn) {
      quotedMsgData = {
        quoteMessageId: quotedMsg.id,
        quoteMessage: removeObjectKeys(quotedMsg, ["handleScrollToBottom"]),
      };
    }
    const {
      msgType = MessageType.Text,
      noNeedInsertToMsgList = false,
      noNeedSendToWebsocket = false,
      senderType = MessageSender.USER,
      status,
    } = opts;
    let msgStatus = noNeedSendToWebsocket
      ? MessageStatus.Success
      : MessageStatus.Sending;
    if (status) {
      msgStatus = status;
    }
    // 生成新的ACK ID用于追踪
    const message = {
      headerType: HeaderType.MESSAGE,
      clientId: createClientUUid(msgType as MessageType),
      content,
      senderType,
      conversationId,
      ...opts,
      type: msgType,
      timestamp: Date.now(),
      status: msgStatus,
      ...quotedMsgData,
    };
    if (noNeedSendToWebsocket) {
      useMessageStore.getState().addOutgoingMessage(message);
      return;
    }

    if (!noNeedInsertToMsgList) {
      // 先在Zustand中添加消息（状态为发送中）
      useMessageStore.getState().addOutgoingMessage(message);
    }

    if (!this.wsClient || !this.isConnected()) {
      // 当发送消息后，socket断开了，会进行重试，重试自动重发，socket依然断开，则直接返回失败
      const valid = await autoRetryMessage(
        () => !!this.wsClient && this.isConnected(),
        4,
      );
      if (!valid || !this.wsClient || !this.isConnected()) {
        message.status = MessageStatus.Failed;
        if (!noNeedInsertToMsgList) {
          useMessageStore.getState().updateMessageStatus(message.clientId, {
            status: MessageStatus.Failed,
          });
        }
        return;
      }
    }

    if (
      ![
        MessageType.Audio,
        MessageType.Video,
        MessageType.Image,
        MessageType.File,
        MessageNotRender.RealTime,
      ].includes(msgType as MessageType)
    ) {
      setQuotedMsg(undefined);
    }

    // 通过WebSocket发送消息
    this.wsClient.sendMessage(message);
  }

  // 关闭连接
  public closeConnection(code?: number) {
    if (this.wsClient) {
      this.wsClient.close(code);
      this.wsClient = null;
    }
  }

  public handleReconnectOffline() {
    if (this.wsClient) {
      this.wsClient.connect(true);
    }
  }

  // 检查连接状态
  public isConnected(): boolean {
    return this.wsClient?.getStatus() === SocketConnectionStatus.CONNECTED;
  }

  public getStatus(): SocketConnectionStatus | null {
    return this.wsClient?.getStatus() || null;
  }
}

export const webSocketService = WebSocketService.getInstance();

/**
 * 消息自动重发方法
 */
export async function autoRetryMessage(
  sendMessageFn: () => boolean,
  maxRetries: number = 3,
  retryDelay: number = 3000,
): Promise<boolean> {
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      const valid = sendMessageFn();
      if (valid) {
        return true;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: Error | any) {
      console.error(`第 ${attempt + 1} 次尝试失败：`, error.message);
    }

    // 如果未达到最大尝试次数，则等待后重试
    if (attempt < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
    }

    attempt++;
  }

  console.error(`消息发送失败，已重试 ${maxRetries} 次`);
  return false;
}
